import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { useAttendance } from 'providers/AttendanceContext';
import AttendanceRegularizationDialog from 'components/SS/AttendanceRegularizationDialog';

/* ------------------------ small time helpers ------------------------ */

const toYMD = (d) => new Date(d).toISOString().slice(0, 10); // YYYY-MM-DD

const fmtDuration = (ms) => {
  if (!ms || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => n.toString().padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
};

/* ------------------------ main component ------------------------ */
export default function AttendanceManagement() {
  const {
    isClockedIn,
    punchIn,
    punchOut,
    activeShift,
    history,
    month,
    setMonth,
    fetchHistory,
    loading,
  } = useAttendance();

  const [tick, setTick] = useState(Date.now());
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [open, setOpen] = useState(false);
  const [openCorrection, setOpenCorrection] = useState(false);
  const [func, setFunc] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);
  useEffect(() => {
    const t = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const todayKey = toYMD(new Date());
  const todayRow = useMemo(() => {
    // establish or find today's row (completed or in-progress)
    const row = history.find((r) => r.date === todayKey);
    if (row) return row;
    // if active shift exists today, display a virtual row for UI
    if (activeShift && toYMD(activeShift?.loginTime) === todayKey) {
      return {
        id: 'today-virtual',
        date: todayKey,
        punchIn: activeShift?.loginTime,
        punchOut: null,
        durationMs: tick - activeShift?.loginTime,
        status: 'In Progress',
      };
    }
    return null;
  }, [history, activeShift, tick, todayKey]);

  // monthly summary
  const monthKey = new Date().toISOString().slice(0, 7); // YYYY-MM
  const monthRows = history.filter((r) => r.date.startsWith(monthKey));
  const monthHoursMs = monthRows.reduce((acc, r) => acc + (r.durationMs || 0), 0);
  const monthDays = new Set(monthRows.map((r) => r.date)).size;

  const handleClose = () => {
    setOpen(!open);
    if (func == 'punchIn') {
      punchIn();
    } else if (func == 'punchOut') {
      punchOut();
    }
    setFunc(null);
  };

  return (
    <>
      <Box component="section" sx={{ p: { xs: 3, md: 3 } }}>
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12, md: 5 }}>
            <Paper sx={{ p: { xs: 3, md: 4 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h6" fontWeight={700}>
                  Attendance
                </Typography>
                <Chip
                  label={isClockedIn ? 'Clocked In' : 'Clocked Out'}
                  color={isClockedIn ? 'success' : 'default'}
                  variant="filled"
                  size="small"
                />
              </Stack>

              <Typography variant="h3" mt={2} fontWeight={700}>
                {new Date(tick).toLocaleTimeString('en-IN', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </Typography>

              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {new Date().toLocaleDateString('en-IN', {
                  weekday: 'short',
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Button
                  variant="contained"
                  color="success"
                  disabled={isClockedIn || activeShift}
                  loading={loading}
                  onClick={() => {
                    setOpen(!open);
                    setFunc('punchIn');
                  }}
                >
                  Punch In
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!isClockedIn}
                  loading={loading}
                  onClick={() => {
                    setOpen(!open);
                    setFunc('punchOut');
                  }}
                >
                  Punch Out
                </Button>
              </Stack>

              {/* Elapsed timer while clocked in */}
              <Paper
                background={1}
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 2,

                  bgcolor: (t) => {
                    return t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#F6FAFF';
                  },
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  {isClockedIn ? 'You’re currently working' : 'Not working right now'}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="baseline">
                  <Typography variant="h4" fontWeight={800}>
                    {isClockedIn
                      ? fmtDuration(tick - new Date(activeShift?.loginTime))
                      : '00:00:00'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    elapsed
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2} mt={1}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`In: ${activeShift?.loginTime ? moment(activeShift?.loginTime).format('hh:mm A') : '—'}`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Out: ${activeShift?.logoutTime ? moment(activeShift?.logoutTime).format('hh:mm A') : '—'}`}
                  />
                </Stack>
              </Paper>

              {/* Today snapshot */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} mb={1}>
                  Today
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  divider={<Divider flexItem orientation="vertical" />}
                  sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default' }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Punch In
                    </Typography>
                    <Typography fontWeight={600}>
                      {activeShift?.loginTime
                        ? moment(activeShift?.loginTime).format('hh:mm A')
                        : '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Punch Out
                    </Typography>
                    <Typography fontWeight={600}>
                      {activeShift?.logoutTime
                        ? moment(activeShift?.logoutTime).format('hh:mm A')
                        : '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total
                    </Typography>
                    <Typography fontWeight={600}>
                      {activeShift?.workingHour ? `${activeShift?.workingHour}h` : '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Typography fontWeight={600}>{activeShift?.status || 'pending'}</Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Month summary */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} mb={1}>
                  This Month
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label={`Days: ${monthDays}`} />
                  <Chip label={`Hours: ${fmtDuration(monthHoursMs)}`} />
                </Stack>
              </Box>
            </Paper>
          </Grid>
          <Grid item size={{ xs: 12, md: 7 }}>
            <Paper sx={{ p: { xs: 2, md: 3 } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={1.5}
                mb={2}
              >
                <Typography variant="h6" fontWeight={700}>
                  Attendance History
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" onClick={() => setOpenCorrection(true)}>
                    Correction
                  </Button>
                  <Box sx={{ pr: 2, width: '70%' }}>
                    <TextField
                      type="month"
                      variant="filled"
                      fullWidth
                      label="Month Of Attendance"
                      value={month}
                      onChange={(e) => {
                        setMonth(e.target.value);
                        // const [yyyy, mm] = e.target.value.split('-');
                        // setMonth(`${mm}/${yyyy}`); // MM/YYYY
                      }}
                      inputProps={{
                        min: '2025-10',
                        max: moment().format('YYYY-MM'), // today's month
                      }}
                    />
                  </Box>
                </Box>
              </Stack>

              <Box sx={{ overflowX: 'auto', overflowY: 'auto', height: '482.5px' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: (t) => (t.palette.mode === 'dark' ? '' : '#F7FAFC') }}>
                      <TableCell sx={{ flex: 1, minWidth: 120 }}>Date</TableCell>
                      <TableCell sx={{ flex: 1, minWidth: 120 }}>Punch In</TableCell>
                      <TableCell sx={{ flex: 1, minWidth: 120 }}>Punch Out</TableCell>
                      <TableCell sx={{ flex: 1, minWidth: 120 }}>Duration</TableCell>
                      <TableCell sx={{ flex: 1, minWidth: 120 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((r, i) => {
                      return (
                        <TableRow key={i + 1}>
                          <TableCell>
                            {r.date}
                            {/* {new Date(r.date).toLocaleDateString('en-IN', {
                            weekday: 'short',
                            day: '2-digit',
                            month: 'short',
                          })} */}
                          </TableCell>
                          <TableCell>
                            {r?.loginTime ? moment(r?.loginTime).format('HH:mm A') : '-'}
                          </TableCell>
                          <TableCell>
                            {r?.logoutTime ? moment(r?.logoutTime).format('HH:mm A') : '-'}
                          </TableCell>
                          <TableCell>{r?.workingHour}h</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={r?.status || '-'}
                              color={r?.status == 'Present' ? 'success' : 'warning'}
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {history.length === 0 && !todayRow && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No records yet. Punch in to get started.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth={'md'}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Are you sure?'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are You Sure You want to {func}.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(!open);
              setFunc(null);
            }}
          >
            No
          </Button>
          <Button onClick={handleClose} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <AttendanceRegularizationDialog
        open={openCorrection}
        onClose={() => setOpenCorrection(false)}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={2400}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
